import Typography, { TypographyProps } from '@mui/material/Typography';

import { WithRef } from '../../../types';
import { mergeSx } from '../../../utils';
import {
  HiddenElementsCounter,
  OverflowCounter,
  OverflowItemTypography,
} from '../../boxes';
import { Chip as DsChip } from '../../chip';
import { InputChipProps } from '../../controls';
import { EllipsisWithTooltip } from '../../tooltip/EllipsisWithTooltip';

type SearchChipProps = Omit<InputChipProps, 'valueProps'> & {
  valueProps?: TypographyProps<'p', { component?: 'p' }>;
};

const valueIsArray = (value: unknown): value is Array<unknown> =>
  Array.isArray(value);

const SearchChip = ({
  ref,
  title,
  value,
  delimiter = ':',
  titleProps,
  valueProps,
  ...rest
}: WithRef<HTMLDivElement, SearchChipProps>) => {
  if (value !== undefined) {
    return (
      <DsChip {...rest} ref={ref}>
        <Typography data-testid="chip-title" {...titleProps}>
          {title}
          {delimiter}{' '}
        </Typography>

        {valueIsArray(value) ? (
          <OverflowCounter
            display="flex"
            maxWidth={250}
            data-testid="chip-value"
          >
            <Typography {...valueProps} noWrap variant="inherit">
              {value.map((valueItem, index) => (
                <OverflowItemTypography
                  overflowKey={index}
                  key={index}
                  component="span"
                  variant="inherit"
                  className="DsChip-value"
                >{`${valueItem}${
                  index < value.length - 1 ? ', ' : ''
                }`}</OverflowItemTypography>
              ))}
            </Typography>
            <HiddenElementsCounter>
              {(count) =>
                count ? (
                  <Typography
                    data-testid="chip-count"
                    variant="P12B"
                    sx={{
                      width: 'max-content',
                      textWrap: 'nowrap',
                    }}
                  >
                    {' '}
                    +{count} more
                  </Typography>
                ) : null
              }
            </HiddenElementsCounter>
          </OverflowCounter>
        ) : (
          <EllipsisWithTooltip
            data-testid="chip-value"
            TypographyProps={{
              className: 'DsChip-value',
              ...valueProps,
              sx: mergeSx(valueProps?.sx, {
                maxWidth: '250px',
              }),
            }}
          >
            {value.toString().replace('\n', ', ')}
          </EllipsisWithTooltip>
        )}
      </DsChip>
    );
  }

  return (
    <DsChip {...rest} ref={ref}>
      <EllipsisWithTooltip
        data-testid="chip-title"
        TypographyProps={{
          ...valueProps,
          sx: mergeSx(valueProps?.sx, {
            maxWidth: '250px',
          }),
        }}
      >
        {title}
      </EllipsisWithTooltip>
    </DsChip>
  );
};

SearchChip.displayName = 'SearchChip';

export default SearchChip;
